import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { initAnalytics, trackPageView } from "@/lib/analytics";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { SmoothScroll } from "@/components/SmoothScroll";
import { Header } from "@/components/Header";
import { SalePopup } from "@/components/SalePopup";
import { Footer } from "@/components/Footer";
import { CartDrawer } from "@/components/CartDrawer";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import FestiveSale from "@/pages/FestiveSale";
import ProductDetail from "@/pages/ProductDetail";
import Wishlist from "@/pages/Wishlist";
import Policy from "@/pages/Policy";
import Landing from "@/pages/Landing";
import AdminBanner from "@/pages/AdminBanner";
import AdminHero from "@/pages/AdminHero";
import AdminLeads from "@/pages/AdminLeads";
import AdminAlterations from "@/pages/AdminAlterations";
import AdminReminders from "@/pages/AdminReminders";
import AdminOutfitLeads from "@/pages/AdminOutfitLeads";
import AdminExports from "@/pages/AdminExports";
import OutfitFinder from "@/pages/OutfitFinder";
import Contact from "@/pages/Contact";
import About from "@/pages/About";
import BridesmaidDresses from "@/pages/BridesmaidDresses";
import ReturnsExchange from "@/pages/ReturnsExchange";
import AdminReturns from "@/pages/AdminReturns";
import ShopByCode from "@/pages/ShopByCode";
import AdminVideoCodes from "@/pages/AdminVideoCodes";
import AdminTraffic from "@/pages/AdminTraffic";
import Guide from "@/pages/Guide";
import AdminBulkPieces from "@/pages/AdminBulkPieces";
import { CartReminderPopup } from "@/components/CartReminderPopup";
import { FestiveSignupPopup } from "@/components/FestiveSignupPopup";

const ScrollToTop = () => {
  const { pathname, search } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname, search]);
  useEffect(() => {
    trackPageView(pathname + search);
    // First-party traffic beacon — powers /admin/traffic. No cookies, no IP stored.
    try {
      let sid = sessionStorage.getItem("kc_sid");
      if (!sid) {
        sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
        sessionStorage.setItem("kc_sid", sid);
      }
      navigator.sendBeacon(
        `${process.env.REACT_APP_BACKEND_URL}/api/track`,
        new Blob([JSON.stringify({ path: pathname, ref: document.referrer, sid })], { type: "application/json" })
      );
    } catch { /* beacon is best-effort */ }
  }, [pathname, search]);
  return null;
};

// Legacy Shopify URL patterns (from kurticonnectionusa.com) → new storefront paths.
// Ensures old product/collection links keep working after the domain redirect.
const LegacyProductRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/product/${slug}`} replace />;
};

const LegacyCollectionRedirect = () => {
  const { handle } = useParams();
  return <Navigate to={`/shop?collection=${handle}`} replace />;
};

function App() {
  useEffect(() => { initAnalytics(); }, []);
  // Google Merchant Center store widget (store rating / quality badge).
  // LEFT_BOTTOM so it never overlaps the floating WhatsApp button (bottom-right).
  useEffect(() => {
    if (document.getElementById("merchantWidgetScript")) return;
    const s = document.createElement("script");
    s.id = "merchantWidgetScript";
    s.src = "https://www.gstatic.com/shopping/merchant/merchantwidget.js";
    s.defer = true;
    s.addEventListener("load", () => {
      if (window.merchantwidget) {
        window.merchantwidget.start({ merchant_id: 5851073123, position: "LEFT_BOTTOM" });
      }
    });
    document.head.appendChild(s);
  }, []);
  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <CartProvider>
          <WishlistProvider>
          <SmoothScroll>
            <ScrollToTop />
            <Header />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/festive-sale" element={<FestiveSale />} />
                <Route path="/diwali-sale" element={<Navigate to="/festive-sale" replace />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/product/:slug" element={<ProductDetail />} />
                <Route path="/wishlist" element={<Wishlist />} />
                <Route path="/policies/:handle" element={<Policy />} />
                <Route path="/products/:slug" element={<LegacyProductRedirect />} />
                <Route path="/products" element={<Navigate to="/shop" replace />} />
                <Route path="/collections/:handle" element={<LegacyCollectionRedirect />} />
                <Route path="/collections" element={<Navigate to="/shop" replace />} />
                <Route path="/admin/banner" element={<AdminBanner />} />
                <Route path="/admin/hero" element={<AdminHero />} />
                <Route path="/admin/leads" element={<AdminLeads />} />
                <Route path="/admin/alterations" element={<AdminAlterations />} />
                <Route path="/admin/reminders" element={<AdminReminders />} />
                <Route path="/admin/outfit-leads" element={<AdminOutfitLeads />} />
                <Route path="/admin/exports" element={<AdminExports />} />
                <Route path="/outfit-finder" element={<OutfitFinder />} />
                <Route path="/find" element={<Navigate to="/outfit-finder" replace />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/about" element={<About />} />
                <Route path="/bridesmaid-dresses" element={<BridesmaidDresses />} />
                <Route path="/returns" element={<ReturnsExchange />} />
                <Route path="/admin/returns" element={<AdminReturns />} />
                <Route path="/shop-code" element={<ShopByCode />} />
                <Route path="/admin/video-codes" element={<AdminVideoCodes />} />
                <Route path="/admin/traffic" element={<AdminTraffic />} />
                <Route path="/guide/:slug" element={<Guide />} />
                <Route path="/admin/bulk-pieces" element={<AdminBulkPieces />} />
                <Route path="/:slug" element={<Landing />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </main>
            <Footer />
            <CartDrawer />
            <WhatsAppButton />
            <SalePopup />
            <CartReminderPopup />
            <FestiveSignupPopup />
            <Toaster position="bottom-right" toastOptions={{ style: { fontFamily: "Manrope, sans-serif", borderRadius: 0 } }} />
          </SmoothScroll>
          </WishlistProvider>
        </CartProvider>
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;
